/**
 * Route guard components for protected routes
 */

import { Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from './auth-store';
import { AuthPolicy } from './types';
import { hasPermission } from './permissions';

interface RequireAuthProps {
  children?: React.ReactNode;
  redirectTo?: string;
}

/**
 * Require user to be authenticated
 * Redirects to login if not authenticated
 */
export function RequireAuth({ children, redirectTo = '/login' }: RequireAuthProps) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  return children ? <>{children}</> : <Outlet />;
}

interface RequirePermissionProps {
  policy: AuthPolicy;
  children?: React.ReactNode;
  redirectTo?: string;
  fallback?: React.ReactNode;
}

/**
 * Require user to have specific permission
 * Shows fallback or redirects if user doesn't have permission
 */
export function RequirePermission({ 
  policy, 
  children, 
  redirectTo = '/unauthorized',
  fallback 
}: RequirePermissionProps) {
  const { user, isLoading } = useAuthStore();

  if (isLoading) {
    return <LoadingScreen />;
  }

  const hasAccess = hasPermission(user, policy);

  if (!hasAccess) {
    if (fallback) {
      return <>{fallback}</>;
    }
    return <Navigate to={redirectTo} replace />;
  }

  return children ? <>{children}</> : <Outlet />;
}

/**
 * Simple loading screen component
 * TODO: You can replace this with your own loading component
 */
function LoadingScreen() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        <p className="text-gray-600">Loading...</p>
      </div>
    </div>
  );
}
